import type { AppRecord } from '../../lib/types';
import type { CompassLocale } from '../i18n';

export function AboutView(props: {
  apps: AppRecord[];
  locale: CompassLocale;
}): React.JSX.Element {
  const { apps, locale } = props;
  const text = locale === 'zh-CN'
    ? {
        title: '关于 Compass Gallery',
        description: 'Compass Gallery 把 Chariot 工作流生成的应用、Prompt、文档、QA 信号和部署产物聚合到一个可浏览、可验证、可复用的界面里。',
        sections: [
          {
            title: '统一展示层',
            body: '应用截图、提示词、Scribe 文档、Axle 资源与 Deploy 信息集中呈现，减少跨目录追踪成本。',
          },
          {
            title: '质量信号',
            body: 'Harness 分数、流水线状态、验收结果和运行态健康信息直接进入页面，不再只留在构建日志里。',
          },
          {
            title: '复用入口',
            body: 'Prompt Library 和 App Demos 让同一份工作流结果既能被回看，也能被直接拿来继续迭代。',
          },
        ],
        stats: [
          ['Apps', String(apps.length)],
          ['Prompts', String(apps.filter((app) => app.promptText.trim().length > 0).length)],
          ['Runnable', String(apps.filter((app) => app.deploy.present || app.preview.url).length)],
        ],
      }
    : {
        title: 'About Compass Gallery',
        description: 'Compass Gallery aggregates apps, prompts, docs, QA signals, and deploy artifacts produced by Chariot workflows into a browsable, verifiable surface.',
        sections: [
          {
            title: 'Unified Showcase',
            body: 'Screenshots, prompts, Scribe docs, Axle resources, and deploy metadata live in one surface instead of scattered output folders.',
          },
          {
            title: 'Quality Signals',
            body: 'Harness scores, pipeline status, acceptance results, and runtime health appear in the UI instead of staying buried in logs.',
          },
          {
            title: 'Reuse Paths',
            body: 'Prompt Library and App Demos turn the same workflow outputs into materials for review, validation, and iteration.',
          },
        ],
        stats: [
          ['Apps', String(apps.length)],
          ['Prompts', String(apps.filter((app) => app.promptText.trim().length > 0).length)],
          ['Runnable', String(apps.filter((app) => app.deploy.present || app.preview.url).length)],
        ],
      };

  return (
    <main className="flex-grow max-w-[1280px] mx-auto w-full px-4 md:px-10 py-12 pt-[100px] flex flex-col gap-12">
      <header className="max-w-3xl">
        <h1 className="font-geist text-[48px] leading-[1.1] font-bold text-on-surface mb-4">{text.title}</h1>
        <p className="text-lg leading-relaxed text-on-surface-variant">{text.description}</p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {text.stats.map(([label, value]) => (
          <div key={label} className="glass-card rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-cyan mb-3">{label}</div>
            <div className="font-geist text-4xl font-bold text-on-surface">{value}</div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {text.sections.map((section) => (
          <article key={section.title} className="glass-card rounded-2xl p-8 flex flex-col gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary">layers</span>
            </div>
            <h2 className="font-geist text-2xl font-semibold text-on-surface">{section.title}</h2>
            <p className="text-sm leading-relaxed text-on-surface-variant">{section.body}</p>
          </article>
        ))}
      </section>

      <section className="glass-panel-l2 rounded-[32px] p-10 flex flex-col gap-6">
        <h2 className="font-geist text-3xl font-semibold text-on-surface">Compass Gallery</h2>
        <p className="max-w-4xl text-on-surface-variant leading-relaxed">
          {locale === 'zh-CN'
            ? '它不是单纯的截图墙，而是把应用结果、生成依据、文档说明和运行质量串成一个可审查的产品界面。这个页面结构直接对应 Stitch 里的 Home / Gallery / Library / Detail / About 设计拆分。'
            : 'This is not just a screenshot wall. It turns app outputs, generation inputs, documentation, and runtime quality into a reviewable product surface. The page structure maps directly to the Home / Gallery / Library / Detail / About breakdown from the Stitch design.'}
        </p>
      </section>
    </main>
  );
}
